import { useState } from 'react';

import type { Workspace } from '../../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import type { S3Object } from '../../../drive/types';
import { useWorkspaces } from '../../Orchestrator/WorkspaceProvider';
import { ensureTrailingSlash, getDisplayName, getLastPathSegment, isDirectory } from '../helpers';
import {
  copyDirectoryAcrossWorkspaces,
  copyFileAcrossWorkspaces,
  type CrossWorkspaceConfig,
  moveDirectoryAcrossWorkspaces,
  moveFileAcrossWorkspaces,
} from '../helpers/crossWorkspaceS3';
import { OperationType } from './useOperationErrors';

export interface CopyMoveOperation {
  key: string;
  name: string;
  isDirectory: boolean;
  operation: 'copy' | 'move';
}

export interface UseFileOperationsProps {
  s3Service: {
    listAllObjects: (prefix?: string) => Promise<S3Object[]>;
    copyFile: (sourceKey: string, destinationKey: string, refreshPrefix?: string) => Promise<void>;
    copyDirectory: (sourceKey: string, destinationKey: string, refreshPrefix?: string) => Promise<void>;
    moveFile: (sourceKey: string, destinationKey: string, refreshPrefix?: string) => Promise<void>;
    moveDirectory: (sourceKey: string, destinationKey: string, refreshPrefix?: string) => Promise<void>;
    downloadFile: (key: string) => Promise<Blob>;
  };
  stsDriveUrl?: string;
  workspaceDriveUrl?: string;
  onOperationComplete?: () => void;
  onOperationError?: (type: OperationType, message: string) => void;
}

export const useFileOperations = ({
  s3Service,
  stsDriveUrl,
  workspaceDriveUrl,
  onOperationComplete,
  onOperationError,
}: UseFileOperationsProps) => {
  const { activeWorkspace } = useWorkspaces();
  const [destinationModalOpen, setDestinationModalOpen] = useState(false);
  const [copyMoveOperation, setCopyMoveOperation] = useState<CopyMoveOperation | null>(null);
  const [allWorkspaceObjects, setAllWorkspaceObjects] = useState<S3Object[]>([]);
  const [isOperationInProgress, setIsOperationInProgress] = useState(false);

  const prepareCopyMoveOperation = async (key: string, operation: 'copy' | 'move') => {
    const isDir = isDirectory(key);
    const displayName = getDisplayName(key);

    if (s3Service) {
      try {
        const allObjects = await s3Service.listAllObjects();
        setAllWorkspaceObjects(allObjects);
      } catch (error) {
        console.error('Failed to fetch all objects:', error);
      }
    }

    setCopyMoveOperation({
      key,
      name: displayName,
      isDirectory: isDir,
      operation,
    });
    setDestinationModalOpen(true);
  };

  const handleCopyFile = async (key: string) => {
    await prepareCopyMoveOperation(key, 'copy');
  };

  const handleMoveFile = async (key: string) => {
    await prepareCopyMoveOperation(key, 'move');
  };

  const handleDownloadFile = async (key: string) => {
    try {
      const blob = await s3Service.downloadFile(key);
      const filename = getLastPathSegment(key) || 'download';

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error occurred';
      console.error('Failed to download file:', error);
      onOperationError?.('download', `Failed to download file: ${errorMessage}`);
    }
  };

  const handleDestinationModalClose = () => {
    setDestinationModalOpen(false);
    setCopyMoveOperation(null);
  };

  const createWorkspaceConfig = (workspaceId: string): CrossWorkspaceConfig => {
    if (!stsDriveUrl || !workspaceDriveUrl) {
      throw new Error('Drive URLs not configured for cross-workspace operations');
    }
    return { workspaceId, stsDriveUrl, workspaceDriveUrl };
  };

  const executeCrossWorkspaceOperation = async (
    sourceConfig: CrossWorkspaceConfig,
    destinationConfig: CrossWorkspaceConfig,
    sourceKey: string,
    destinationKey: string,
    isDirectory: boolean,
    operation: 'copy' | 'move'
  ) => {
    const operationMap = {
      file: { copy: copyFileAcrossWorkspaces, move: moveFileAcrossWorkspaces },
      directory: { copy: copyDirectoryAcrossWorkspaces, move: moveDirectoryAcrossWorkspaces },
    };

    const operationType = isDirectory ? 'directory' : 'file';
    const operationFn = operationMap[operationType][operation];

    await operationFn(sourceConfig, destinationConfig, sourceKey, destinationKey);
  };

  const handleDestinationModalConfirm = async (destinationPath: string, destinationWorkspace?: Workspace) => {
    if (!copyMoveOperation) return;

    const isCrossWorkspace = destinationWorkspace && destinationWorkspace.name !== activeWorkspace?.name;

    try {
      setIsOperationInProgress(true);

      const { key, isDirectory, operation } = copyMoveOperation;
      const sourceName = getLastPathSegment(key);
      const normalizedDestinationPath = destinationPath ? ensureTrailingSlash(destinationPath) : '';
      const destinationKey = `${normalizedDestinationPath}${sourceName}`;

      if (isCrossWorkspace) {
        if (!activeWorkspace?.name || !destinationWorkspace?.name) {
          throw new Error('Workspace information not available');
        }

        const sourceConfig = createWorkspaceConfig(activeWorkspace.name);
        const destinationConfig = createWorkspaceConfig(destinationWorkspace.name);

        await executeCrossWorkspaceOperation(
          sourceConfig,
          destinationConfig,
          key,
          destinationKey,
          isDirectory,
          operation
        );
      } else {
        // Same workspace operation: use S3 copy command
        const operationMethod = isDirectory
          ? operation === 'copy'
            ? s3Service.copyDirectory
            : s3Service.moveDirectory
          : operation === 'copy'
          ? s3Service.copyFile
          : s3Service.moveFile;

        await operationMethod(key, destinationKey);
      }

      setDestinationModalOpen(false);
      setCopyMoveOperation(null);
      onOperationComplete?.();
    } catch (error) {
      console.error(`${copyMoveOperation.operation} operation failed:`, error);
      const errorMessage =
        error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error occurred';
      onOperationError?.(copyMoveOperation.operation, `Failed to ${copyMoveOperation.operation}: ${errorMessage}`);
    } finally {
      setIsOperationInProgress(false);
    }
  };

  const refreshAllObjects = async () => {
    if (s3Service) {
      try {
        const allObjects = await s3Service.listAllObjects();
        setAllWorkspaceObjects(allObjects);
      } catch (error) {
        console.error('Failed to refresh all objects:', error);
      }
    }
  };

  return {
    destinationModalOpen,
    copyMoveOperation,
    allWorkspaceObjects,
    isOperationInProgress,
    handleCopyFile,
    handleMoveFile,
    handleDownloadFile,
    handleDestinationModalClose,
    handleDestinationModalConfirm,
    refreshAllObjects,
  };
};
